import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// --- Auto-refresh access_token on 401 so long external flows (Razorpay
// checkout, OAuth) survive our 15-minute access-token TTL.
export const SERVER_BUSY_MSG = "Our server is updating, please try again in a minute.";
const isServerHiccup = (error) => {
  if (!error.response) return error.code !== "ERR_CANCELED";
  const { status, data } = error.response;
  if (status >= 520 && status <= 530) return true;
  if (status < 500) return false;
  const d = data && typeof data === "object" ? data : null;
  return !d || typeof d.detail !== "string" || !!d.cloudflare_error || !!d.ray_id || /cloudflare|origin web server/i.test(d.detail);
};

let _refreshing = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (isServerHiccup(error)) {
      error.message = SERVER_BUSY_MSG;
      if (error.response) error.response.data = { detail: SERVER_BUSY_MSG };
      return Promise.reject(error);
    }
    const original = error.config || {};
    const status = error.response?.status;
    const url = String(original.url || "");
    // Only intercept 401s on our own API, skip the refresh/me/login/logout themselves,
    // and never retry the same request twice.
    if (
      status === 401 &&
      url.startsWith(API) &&
      !original._retried &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/logout")
    ) {
      original._retried = true;
      try {
        _refreshing = _refreshing || axios.post(`${API}/auth/refresh`);
        await _refreshing;
      } catch (e) {
        _refreshing = null;
        return Promise.reject(error);
      } finally {
        setTimeout(() => { _refreshing = null; }, 50);
      }
      return axios(original);
    }
    return Promise.reject(error);
  },
);

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // null = checking, false = anon, object = signed in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, remember = true) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, remember });
    setUser(data);
    return data;
  };

  const googleSession = async (sessionId) => {
    const { data } = await axios.post(
      `${API}/auth/google/session`,
      null,
      { headers: { "X-Session-ID": sessionId } },
    );
    setUser(data);
    return data;
  };

  const startGoogleLogin = () => {
    // Emergent-managed OAuth: redirect to Emergent, come back to /auth/callback with #session_id
    const back = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(back)}`;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, googleSession, startGoogleLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
